import {
    CurrencyEnum,
    DocumentTypeEnum,
    type Employee,
    type Expense,
    ExpenseKindEnum,
    type ExpenseSplit,
    type InvoiceSplit,
    type ItemSplit,
    type OcrResult,
    SplitTypeEnum,
    type Tax,
    type Vendor,
} from "@finway-group/shared/lib/models"
import { ExpenseDocumentTypeEnum } from "@finway-group/shared/lib/models/expense/expenseDocumentType.enum"
import { EXPENSE_KINDS_ALLOWED_FOR_ITEMIZATION } from "@finway-group/shared/lib/models/expense/expenseKind.enum"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { ItemSplitKindEnum, type LineItem } from "@finway-group/shared/lib/models/expense/expenseSplit.model"
import { type OcrResultItem } from "@finway-group/shared/lib/models/ocr/ocr.model"
import { UserLanguageEnum } from "@finway-group/shared/lib/models/user/userSettings.interface"
import { formatCurrencyNumber, insertIf, isPerformanceDateExpenseKind, isPerformanceDateWithinHardLimit, toAmountForCurrency, toDinero } from "@finway-group/shared/lib/utils"
import moment from "moment"
import * as stringSimilarity from "string-similarity"
import { DE_DATE_FORMAT, NO_TAX_RATE, US_DATE_FORMAT } from "~Shared/config/consts"
import { type VendorReadFileAiResponse } from "~Shared/services/ocr.service"
import { checkIsPurchaseRequest, flipCreditNoteAmountSigns, isExpenseArchived, isHospitalityExpense } from "~Shared/utils/expense.utils"
import { capitalizeFirstLetter, formatTaxRate, isEmptyObject, isSet } from "~Shared/utils/helper.utils"
import type {
    CompareDataParams,
    CompareFormOcrDataParams,
    CompareSplitsDataParams,
    HospitalityWithLabeledAttendees,
    MapSplitsParams,
    OcrObjectKeys,
    OcrObjectSplitsKeys,
    OcrSettings,
    RowInterface,
    SplitDataType,
    UpdateOcrItemsParams,
    UpdatedOcrObject,
} from "~Shared/utils/ocr.utils.types"

const OCR_BEST_MATCHING_RATING = 0.6

export const formatOCRObject = (ocrObject: OcrResult) => {
    const ocrObjectKeys = Object.keys(ocrObject) as Array<OcrObjectKeys>

    const newOcrObject = ocrObject as unknown as UpdatedOcrObject

    ocrObjectKeys.forEach((ocrKey) => {
        switch (ocrKey) {
            case "invoiceDate":
                newOcrObject[ocrKey] = newOcrObject[ocrKey] ? moment(newOcrObject[ocrKey]) : undefined
                break
            case "invoiceDueDate":
                newOcrObject[ocrKey] = newOcrObject[ocrKey] ? moment(newOcrObject[ocrKey]) : undefined
                break
            case "performancePeriod":
                newOcrObject[ocrKey] = newOcrObject[ocrKey]?.map((date) => moment(date))
                break
            case "datePurchased":
                newOcrObject[ocrKey] = moment(newOcrObject[ocrKey])
                break
            default:
                break
        }
    })

    return newOcrObject
}

/**
 * OCR sends the period as a `performancePeriod` tuple, which the api maps onto the stored dates only after its own
 * validation ran, so the hard limit has to be checked here before the update goes out.
 */
export const hasPerformanceDateOutOfHardLimit = ({
    kind,
    datePurchased,
    performancePeriod,
}: {
    kind: ExpenseKindEnum
    datePurchased?: Date | moment.Moment
    performancePeriod?: Array<Date | moment.Moment | undefined>
}) => [...insertIf(isPerformanceDateExpenseKind(kind), datePurchased), ...(performancePeriod ?? [])].some((date) => isSet(date) && !isPerformanceDateWithinHardLimit(date))

export const formatFieldValue = ({
    field,
    value,
    currency,
    userLanguage = UserLanguageEnum.DE,
    resolveCostCenterDisplayName,
    resolveCostCenter2DisplayName,
}: {
    field: OcrObjectKeys | OcrObjectSplitsKeys
    value: any
    currency: CurrencyEnum
    userLanguage?: UserLanguageEnum
    resolveCostCenterDisplayName?: (id: string) => string | undefined
    resolveCostCenter2DisplayName?: (id: string) => string | undefined
}) => {
    if (!isSet(value) && field !== "taxRate") {
        return ""
    }

    switch (field) {
        case "invoiceNumber":
            return `#${value}`
        case "invoiceDate":
            return formatOcrDate(value, userLanguage)
        case "invoiceDueDate":
            return formatOcrDate(value, userLanguage)
        case "totalNetPrice":
            return formatCurrencyNumber({ value: Number(value), currency })
        case "totalTaxPrice":
            return formatCurrencyNumber({ value: Number(value), currency })
        case "totalGrossPrice":
            return formatCurrencyNumber({ value: Number(value), currency })
        case "taxRate":
            if (!value || value._id === NO_TAX_RATE) {
                return "-"
            }
            return `${formatTaxRate(value.taxRate)}%`
        case "taxPrice":
            return formatCurrencyNumber({ value: Number(value), currency })
        case "grossPrice":
            return formatCurrencyNumber({ value: Number(value), currency })
        case "netPrice":
            return formatCurrencyNumber({ value: Number(value), currency })
        case "performancePeriod":
            if (value.includes(undefined) || !value.length) {
                return "-"
            }
            return `${formatOcrDate(value[0], userLanguage)} - ${formatOcrDate(value[1], userLanguage)}`
        case "datePurchased":
            return formatOcrDate(value, userLanguage)
        case "name":
            return capitalizeFirstLetter(value)
        case "vendor":
            return value.name
        case "costCenter":
            return resolveCostCenterDisplayName ? resolveCostCenterDisplayName(value) : value
        case "costCenter2":
            return resolveCostCenter2DisplayName ? resolveCostCenter2DisplayName(value) : value
        default:
            return String(value)
    }
}

export const formatOcrDate = (value: string | Date, userLanguage: UserLanguageEnum) => moment(value).format(userLanguage === UserLanguageEnum.DE ? DE_DATE_FORMAT : US_DATE_FORMAT)

const SPLITS_FIELDS_TO_COMPARE: Array<OcrObjectSplitsKeys> = ["name", "netPrice", "grossPrice", "taxRate", "taxPrice", "unitPrice", "quantity", "costCenter", "costCenter2"]

const hasPercentageSplits = (splits: Array<ExpenseSplit>): boolean => splits.length > 0 && splits.every((split) => "percentage" in split)

/** Percentage splits are replaced by OCR line items — do not diff or merge them with OCR items. */
export const shouldReplaceExpenseSplitsWithOcrItems = (
    splitType: SplitTypeEnum | undefined,
    originalSplits: Array<ExpenseSplit>,
    ocrItems: Array<OcrResultItem> | undefined,
): boolean => Boolean(ocrItems?.length) && (splitType === SplitTypeEnum.SPLIT || hasPercentageSplits(originalSplits))

export const compareSplits = ({
    originalSplits,
    ocrItems,
    t,
    newCurrency,
    originalCurrency,
    resolveCostCenterDisplayName,
    resolveCostCenter2DisplayName,
}: MapSplitsParams): Array<RowInterface> => {
    // Track which OCR items have already been matched to avoid duplicates
    const matchedOcrIndices = new Set<number>()

    // map through original splits and compare with OCR splits
    const splitsData: Array<RowInterface | null> = originalSplits.map((originalSplit, itemIndex) => {
        const hasName = "name" in originalSplit && Boolean((originalSplit.name ?? "").trim())

        // Get available (unmatched) OCR items
        const availableOcrItems = ocrItems.filter((_, index) => !matchedOcrIndices.has(index))

        if (availableOcrItems.length === 0) {
            return null
        }

        let matchedOcrItem: ItemSplit | undefined
        if (hasName) {
            // Get available OCR item names for comparison
            const availableOcrNames = availableOcrItems.map((item: ItemSplit) => item.name?.toLowerCase() || "")

            // Find best match among available items
            const ocrBestMatching = stringSimilarity.findBestMatch(originalSplit.name.toLowerCase(), availableOcrNames)

            const bestMatchRating = ocrBestMatching.bestMatch.rating

            // Check if match is good enough
            if (bestMatchRating < OCR_BEST_MATCHING_RATING) {
                return null
            }

            // Get the actual OCR item (bestMatchIndex refers to availableOcrNames array)
            const bestMatchIndexInAvailable = ocrBestMatching.bestMatchIndex
            matchedOcrItem = availableOcrItems[bestMatchIndexInAvailable]
        } else {
            // Index fallback: reimbursement ITEM rows may exist without a line name before OCR.
            if (itemIndex >= ocrItems.length || matchedOcrIndices.has(itemIndex)) {
                return null
            }
            matchedOcrItem = ocrItems[itemIndex]
        }

        if (!matchedOcrItem) {
            return null
        }

        // Mark this OCR item as matched by finding its index in the original array
        const matchedOcrIndexInOriginal = ocrItems.indexOf(matchedOcrItem)
        matchedOcrIndices.add(matchedOcrIndexInOriginal)

        // Compare the matched items
        const itemParent = compareSplitsData({
            field: "itemParent",
            originalSplit,
            ocrItem: matchedOcrItem,
            newCurrency,
            originalCurrency,
            itemIndex,
            t,
        })

        const itemChildren: Array<RowInterface> = []

        for (const field of SPLITS_FIELDS_TO_COMPARE) {
            const comparedData = compareSplitsData({
                field,
                originalSplit,
                ocrItem: matchedOcrItem,
                newCurrency,
                originalCurrency,
                itemIndex,
                t,
                resolveCostCenterDisplayName,
                resolveCostCenter2DisplayName,
            })
            if (comparedData) {
                itemChildren.push(comparedData)
            }
        }

        // Only return if there are children to show
        if (itemChildren.length && itemParent) {
            return { ...itemParent, children: itemChildren }
        }

        return null
    })

    return splitsData.filter((item): item is RowInterface => item !== null)
}

export const compareSplitsData = ({
    field,
    originalSplit,
    ocrItem,
    newCurrency,
    originalCurrency,
    itemIndex,
    t,
    resolveCostCenterDisplayName,
    resolveCostCenter2DisplayName,
}: CompareSplitsDataParams): RowInterface | void => {
    let originalData = originalSplit[field as keyof ExpenseSplit]
    let ocrData = ocrItem[field as keyof ExpenseSplit]

    if (field === "itemParent") {
        originalData = "name" in originalSplit ? originalSplit.name : ""
        ocrData = ""
        // Reimbursement ITEM lines may have no name yet; show OCR line name in the modal when the original is blank.
        if (!(originalData ?? "").trim()) {
            ocrData = "name" in ocrItem ? (ocrItem.name ?? "") : ""
        }
    }

    const formattedOcrData = formatFieldValue({ field, value: ocrData, currency: newCurrency, resolveCostCenter2DisplayName, resolveCostCenterDisplayName })
    const formattedOriginalData = formatFieldValue({ field, value: originalData, currency: originalCurrency, resolveCostCenter2DisplayName, resolveCostCenterDisplayName })
    const formattedField = t(`info:ocr_updates.fields_names.${field}`)

    const isDifferent = compareOcrAndOriginalData(formattedOriginalData, formattedOcrData)

    if (originalData !== undefined && ocrData !== undefined && isDifferent) {
        return {
            key: itemIndex + field,
            field,
            ocrData,
            itemIndex,
            formattedOriginalData,
            formattedOcrData,
            formattedField,
            split: true,
        }
    }
}

export const compareData = (
    {
        field,
        expense,
        ocrObject,
        foundVendor,
        newCurrency,
        originalCurrency,
        t,
        isHospitality,
        isShowingVendorChangeIfNotFound,
        resolveCostCenterDisplayName,
        resolveCostCenter2DisplayName,
    }: CompareDataParams,
    userLanguage: UserLanguageEnum | undefined,
): RowInterface | void => {
    let originalData = expense[field as keyof Expense]
    let ocrData: any = ocrObject[field as keyof OcrResult]
    let formattedField = t(`info:ocr_updates.fields_names.${field}`)

    if (field === "performancePeriod") {
        originalData = originalData || [expense.performancePeriodStartDate, expense.performancePeriodEndDate]
    }

    if (field === "datePurchased") {
        formattedField = t(`info:ocr_updates.fields_names.${isHospitality ? "hospitalityDate" : "performanceDate"}`)
    }

    if (field === "vendor") {
        const expenseCreditorName = expense.getCreditorName()
        if (expenseCreditorName) {
            originalData = { name: expenseCreditorName }
        }

        if (foundVendor) {
            ocrData = { name: foundVendor.name }
        } else if (!isShowingVendorChangeIfNotFound) {
            ocrData = null
        }
    }

    if (field === "merchant") {
        ocrData = ocrData || ocrObject.vendor?.name
    }

    if (field === "taxRate") {
        originalData = expense.taxRate
        ocrData = isSet(ocrObject?.taxRate) ? ocrObject?.taxRate : (expense.taxRate ?? { _id: NO_TAX_RATE })
    }

    const formattedOcrData = formatFieldValue({ field, value: ocrData, currency: newCurrency, userLanguage, resolveCostCenter2DisplayName, resolveCostCenterDisplayName })
    const formattedOriginalData = formatFieldValue({
        field,
        value: originalData,
        currency: originalCurrency,
        userLanguage,
        resolveCostCenter2DisplayName,
        resolveCostCenterDisplayName,
    })

    const isDifferent = compareOcrAndOriginalData(formattedOriginalData, formattedOcrData)

    if (isSet(ocrData) && isDifferent) {
        return {
            key: field,
            field,
            ocrData,
            formattedOriginalData,
            formattedOcrData,
            formattedField,
        }
    }
}

export const compareFormOcrData = (
    { expense, ocrObject, foundVendor, t, isShowingVendorChangeIfNotFound, resolveCostCenterDisplayName, resolveCostCenter2DisplayName }: CompareFormOcrDataParams,
    loggedInUserLanguage: UserLanguageEnum,
) => {
    const originalCurrency = expense.currency ?? CurrencyEnum.EUR
    const newCurrency = ocrObject.currency ?? originalCurrency

    const isAutoGeneratedCardRequest = expense.isAutoGenerated && expense.paymentOption === ExpensePaymentOptionEnum.SMART_CARD && !expense.isSubscription()
    const isHospitality = isHospitalityExpense(expense)

    // Set the invoice and vendor fields that need to be compared
    const invoiceAndVendorFieldsToBeCompared: Array<OcrObjectKeys> = [
        "invoiceNumber",
        "currency",
        "invoiceDate",
        "invoiceDueDate",
        "vendor",
        "performancePeriod",
        "datePurchased",
        "description",
        "costCenter",
        "costCenter2",
    ]

    // For autoGenerated card requests we disregard the OCR amount prices and tax rate
    const areTotalGrossPricesEqual = Math.abs(expense.totalGrossPrice) === Math.abs(ocrObject.totalGrossPrice ?? 0)
    if (!isAutoGeneratedCardRequest || areTotalGrossPricesEqual) {
        invoiceAndVendorFieldsToBeCompared.push("totalNetPrice", "totalTaxPrice", "totalGrossPrice", "taxRate")
    }
    if (isHospitality) {
        invoiceAndVendorFieldsToBeCompared.push("merchant")
    }

    if (isAutoGeneratedCardRequest) {
        invoiceAndVendorFieldsToBeCompared.splice(invoiceAndVendorFieldsToBeCompared.indexOf("description"), 1)
    }

    const formOCRDataDifferences: Array<RowInterface> = []

    const originalSplits = expense.splits ?? []
    const ocrItems = ocrObject.splits as Array<ItemSplit>

    const shouldUpdateItems = ocrItems && EXPENSE_KINDS_ALLOWED_FOR_ITEMIZATION.includes(expense.kind)

    if (shouldUpdateItems && !shouldReplaceExpenseSplitsWithOcrItems(expense.splitType, originalSplits, ocrItems)) {
        const splits = compareSplits({
            originalSplits,
            ocrItems,
            t,
            newCurrency,
            originalCurrency,
            resolveCostCenterDisplayName,
            resolveCostCenter2DisplayName,
        }).filter((item) => !isEmptyObject(item))
        if (splits?.length) {
            formOCRDataDifferences.push(...splits)
        }
    }

    const documentTypeDifference = compareDocumentType({ expense, ocrObject, t })
    if (documentTypeDifference) {
        formOCRDataDifferences.push(documentTypeDifference)
    }

    for (const field of invoiceAndVendorFieldsToBeCompared) {
        const comparedData = compareData(
            {
                field,
                expense,
                ocrObject,
                foundVendor,
                newCurrency,
                originalCurrency,
                t,
                isHospitality,
                isShowingVendorChangeIfNotFound,
                resolveCostCenterDisplayName,
                resolveCostCenter2DisplayName,
            },
            loggedInUserLanguage,
        )
        if (comparedData) {
            formOCRDataDifferences.push(comparedData)
        }
    }

    if (formOCRDataDifferences.length === 0) {
        return
    }

    const sortedDataDifference = formOCRDataDifferences.sort((a) => (a.field === "itemParent" ? 1 : -1))

    return sortedDataDifference
}

export const mergeExistingAndOcrItems = (existingItems: Array<ExpenseSplit>, ocrItems: Array<OcrResultItem>, splitType?: SplitTypeEnum) => {
    if (shouldReplaceExpenseSplitsWithOcrItems(splitType, existingItems, ocrItems)) {
        return ocrItems
    }

    const noEmptyExistingItems = existingItems.filter((item: ItemSplit) => item.netPrice !== 0)
    const expenseItemsNames = noEmptyExistingItems.map((split: ItemSplit) => split.name?.toLowerCase())
    const notRepeatedOcrDataItems = ocrItems.filter((item: ItemSplit) => !expenseItemsNames.includes(item.name?.toLowerCase()))
    const tipItems = existingItems.filter((item: ItemSplit) => item.kind === ItemSplitKindEnum.TIP)

    return [...noEmptyExistingItems, ...notRepeatedOcrDataItems, ...tipItems]
}

export const compareOcrAndOriginalData = (originalData: string, ocrData: string) => originalData?.toLowerCase() !== ocrData?.toLowerCase()

export const updateOcrItems = ({ ocrItemizationEnabled, ocrObject, expense }: UpdateOcrItemsParams): SplitDataType => {
    const shouldUpdateItems = ocrItemizationEnabled && ocrObject.splits && EXPENSE_KINDS_ALLOWED_FOR_ITEMIZATION.includes(expense.kind)

    if (shouldUpdateItems) {
        const isPurchaseRequest = checkIsPurchaseRequest(expense)
        if (expense.splitType === SplitTypeEnum.ITEM || expense.kind === ExpenseKindEnum.HOSPITALITY || expense.isReimbursement || isPurchaseRequest) {
            return {
                splitType: SplitTypeEnum.ITEM,
                splits: ocrObject.splits?.map((split: ItemSplit) => ({
                    _id: split._id,
                    taxRate: split.taxRate,
                    netPrice: split.netPrice,
                    taxPrice: split.taxPrice,
                    grossPrice: split.grossPrice,

                    costCenter: split.costCenter ?? expense.costCenter ?? ocrObject.costCenter,
                    costCenter2: split.costCenter2 ?? expense.costCenter2 ?? ocrObject.costCenter2,
                    ...(expense.kind !== ExpenseKindEnum.HOSPITALITY && {
                        expenseAccount: expense.expenseAccount,
                    }),
                    // specific to item split
                    name: split.name ?? "Item",
                    kind: split.kind,
                })),
            }
        }
        const firstSplit = ocrObject.splits?.[0]
        // only to filter out the hidden percentage split after OCR run.
        if (firstSplit && !("name" in firstSplit) && !("purchaseOrderLink" in firstSplit) && "percentage" in firstSplit) {
            ocrObject.splits?.shift()
        }
        return {
            splitType: SplitTypeEnum.LINE_ITEM,
            splits: ocrObject.splits?.map((split: LineItem) => ({
                _id: split._id,
                taxRate: split.taxRate,
                netPrice: split.netPrice,
                taxPrice: split.taxPrice,
                grossPrice: split.grossPrice,

                costCenter: split.costCenter ?? expense.costCenter ?? ocrObject.costCenter,
                costCenter2: split.costCenter2 ?? expense.costCenter2 ?? ocrObject.costCenter2,
                expenseAccount: expense.expenseAccount,
                // specific to line item split
                name: split.name ?? "Item",
                quantity: split.quantity,
                unitOfMeasure: split.unitOfMeasure,
                unitPrice: split.unitPrice,
                materialCode: split.materialCode,
                purchaseOrderLink: split.purchaseOrderLink,
            })),
        }
    }

    if ([SplitTypeEnum.LINE_ITEM, SplitTypeEnum.ITEM].includes(expense.splitType)) {
        return undefined
    }
    return {
        splits: expense.splits?.map((split: InvoiceSplit) => ({
            ...split,
            netPrice: (ocrObject.totalNetPrice ?? expense.totalNetPrice ?? 0) * (split.percentage / 100),
            taxPrice: (ocrObject.totalTaxPrice ?? expense.totalTaxPrice ?? 0) * (split.percentage / 100),
            taxRate: ocrObject.taxRate ? (ocrObject.taxRate as InvoiceSplit["taxRate"]) : expense.taxRate ? expense.taxRate : { _id: NO_TAX_RATE },
            grossPrice: (ocrObject.totalGrossPrice ?? expense.totalGrossPrice ?? 0) * (split.percentage / 100),
            costCenter: split.costCenter ?? expense.costCenter ?? ocrObject.costCenter,
            costCenter2: split.costCenter2 ?? expense.costCenter2 ?? ocrObject.costCenter2,
        })),
    }
}

/** When OCR itemization is off, split-level CC might not be surfaced; merge header OCR CC onto existing splits. */
export const mergeHeaderCostCentersOntoSplitsWhenItemizationDisabled = (
    splits: Array<ExpenseSplit> | undefined,
    ocrObject: Pick<OcrResult, "costCenter" | "costCenter2">,
): Array<ExpenseSplit> | undefined => {
    if (!splits?.length) {
        return splits
    }
    const header1 = ocrObject.costCenter
    const header2 = ocrObject.costCenter2
    if (header1 === undefined && header2 === undefined) {
        return splits
    }
    return splits.map((split) => ({
        ...split,
        ...(header1 !== undefined && { costCenter: split.costCenter ?? header1 }),
        ...(header2 !== undefined && { costCenter2: split.costCenter2 ?? header2 }),
    }))
}

export const updatingAttendees = (existingData: Expense | HospitalityWithLabeledAttendees, requester?: Employee) => {
    const areExistingAttendees = "attendees" in existingData && existingData.attendees?.length > 0

    if (!areExistingAttendees && !requester) {
        return
    }

    if (areExistingAttendees && !requester) {
        return existingData.attendees
    }

    if (requester) {
        const requesterObject = { value: requester.id, label: requester.name }

        if (areExistingAttendees) {
            const containsRequester = existingData.attendees.find(({ value }) => value === requesterObject.value)
            return containsRequester ? existingData.attendees : [...existingData.attendees, requesterObject]
        }

        return [requesterObject]
    }

    return undefined
}

const shouldUpdatePricesForAutoGeneratedCardRequests = (expense: Expense, ocrObject: OcrResult) => {
    if (!ocrObject.totalNetPrice || !ocrObject.totalTaxPrice) {
        return false
    }

    const ocrSum = toAmountForCurrency(toDinero(Math.abs(ocrObject.totalTaxPrice)).add(toDinero(Math.abs(ocrObject.totalNetPrice))), ocrObject.currency || expense.currency)
    const expenseGrossPrice = Math.abs(expense.totalGrossPrice)

    return toDinero(ocrSum).equalsTo(toDinero(expenseGrossPrice))
}

// For autoGenerated card requests we disregard the OCR splits
// And if the OCR total net price + total tax price is not equal to the expense total gross price, it doesn't update "totalNetPrice", "totalTaxPrice", "taxRate" too
export const formatOcrObjectForAutoGeneratedCardRequests = (expense: Expense, ocrObject: OcrResult) => {
    const shouldUpdatePrices = shouldUpdatePricesForAutoGeneratedCardRequests(expense, ocrObject)

    const fieldsNotToBeUpdatedOnAutoGeneratedCardRequest = shouldUpdatePrices ? ["description"] : ["totalNetPrice", "totalGrossPrice", "totalTaxPrice", "taxRate", "description"]

    if (expense.splitType === SplitTypeEnum.SPLIT) {
        fieldsNotToBeUpdatedOnAutoGeneratedCardRequest.push("splits", "splitType")
    }

    const ocrObjectToFormat = { ...ocrObject }

    for (const field of fieldsNotToBeUpdatedOnAutoGeneratedCardRequest) {
        delete ocrObjectToFormat[field as keyof OcrResult]
    }
    return formatOCRObject(ocrObjectToFormat)
}

// Rates inferred from cent amounts carry rounding error (e.g. 18.96 for a real 19%). Only for those
// (allowToleranceMatch) do we fall back to the closest configured rate within this tolerance after an
// exact match fails. A rate read explicitly off the invoice must match exactly or not at all — never
// snap 10.22% onto a configured 10% (BUG-12023).
const TAX_RATE_MATCH_TOLERANCE = 0.5

export const getPreferredTaxByRate = ({ taxRate, taxes, allowToleranceMatch = false }: { taxRate: number; taxes: Array<Tax>; allowToleranceMatch?: boolean }): Tax | undefined => {
    const activeTaxes = [...taxes].sort((a, b) => Number(b.isDefault ?? false) - Number(a.isDefault ?? false)).filter((tax) => tax.deleted === false)
    const exactMatch = activeTaxes.find((tax) => tax.taxRate === taxRate)

    if (exactMatch || !allowToleranceMatch) {
        return exactMatch
    }

    return activeTaxes.filter((tax) => Math.abs(tax.taxRate - taxRate) <= TAX_RATE_MATCH_TOLERANCE).sort((a, b) => Math.abs(a.taxRate - taxRate) - Math.abs(b.taxRate - taxRate))[0]
}

export const initializeOcrSettings = (loggedInProfile: Employee): OcrSettings => {
    const isOcrEnabled = loggedInProfile.settings.enableOcr
    const isOcrItemizationEnabled = loggedInProfile.settings.enableOcrItemization
    const isOcrItemizationDisplayed = true

    return {
        isOcrEnabled,
        isOcrItemizationEnabled,
        isOcrItemizationDisplayed,
    }
}

/** Hospitality expenses, self-issued receipts and archived expenses never run OCR on the documents that are attached to them. */
const isOcrBlockedForExpense = (expense: Expense): boolean => isHospitalityExpense(expense) || Boolean(expense.isSelfIssuedReceipt) || isExpenseArchived(expense)

/** The OCR toggles belong to one expense: an expense that blocks OCR switches them off, every other expense falls back to the employee defaults. */
export const resolveOcrSettingsForExpense = ({ expense, defaultSettings }: { expense: Expense; defaultSettings: OcrSettings }): OcrSettings => {
    const isOcrBlocked = isOcrBlockedForExpense(expense)

    return {
        ...defaultSettings,
        isOcrEnabled: !isOcrBlocked && defaultSettings.isOcrEnabled,
        isOcrItemizationEnabled: !isOcrBlocked && defaultSettings.isOcrItemizationEnabled,
    }
}

export const mapVendorReadFileAiResponseToVendor = (ocrData: VendorReadFileAiResponse | undefined): Partial<Vendor> | undefined => {
    const isExistingVendorDetected = ocrData?.vendorId
    if (isExistingVendorDetected) {
        return undefined
    }

    switch (ocrData?.ocrData?._discriminator) {
        case DocumentTypeEnum.PURCHASE_ORDER:
            const poSupplier = ocrData?.ocrData?.supplier

            return {
                name: poSupplier?.name,
                // tax
                vatNumber: poSupplier?.vatNumber,
                // bank
                iban: poSupplier?.ibanNumber,
                bic: poSupplier?.bicCode,
                // address
                streetName: poSupplier?.streetName,
                streetNumber: poSupplier?.streetNumber,
                city: poSupplier?.city,
                zip: poSupplier?.zipCode,
                // contact
                contactPersonName: poSupplier?.contactPerson,
                contactPersonPhone: poSupplier?.phoneNumber,
                contactPersonEmail: poSupplier?.email,
            }

        case DocumentTypeEnum.DELIVERY_NOTE:
            const dnSupplier = ocrData?.ocrData?.supplier

            return {
                name: dnSupplier?.name,
                vatNumber: dnSupplier?.vatId,
            }
        default:
            return undefined
    }
}

/**
 * OCR assigns a credit note under the same conditions as the document type selector, never on a self-issued receipt
 * and never over a type the user picked by hand.
 */
export const shouldApplyOcrCreditNote = ({
    ocrObject,
    expense,
    isNettingEnabled,
    isDocumentTypeSetByUser = false,
}: {
    ocrObject: Pick<OcrResult, "documentType">
    expense: Pick<Expense, "paymentOption" | "isSelfIssuedReceipt" | "isReimbursement">
    isNettingEnabled: boolean
    isDocumentTypeSetByUser?: boolean
}) =>
    ocrObject.documentType === ExpenseDocumentTypeEnum.CREDIT_NOTE &&
    isNettingEnabled &&
    !expense.isReimbursement &&
    expense.paymentOption !== ExpensePaymentOptionEnum.SMART_CARD &&
    !expense.isSelfIssuedReceipt &&
    !isDocumentTypeSetByUser

/**
 * The API returns a detected credit note negated, as it is stored. The OCR flows compare and merge it with the expense as printed
 * on the document, the way the form shows it, and only keep the credit note type where it may be applied. Callers convert the
 * merged result back with flipCreditNoteAmountSigns once the resulting document type is known.
 */
export const toOcrResultForExpense = ({
    ocrObject,
    expense,
    isNettingEnabled,
    isDocumentTypeSetByUser,
}: {
    ocrObject: OcrResult
    expense: Pick<Expense, "paymentOption" | "isSelfIssuedReceipt" | "isReimbursement">
    isNettingEnabled: boolean
    isDocumentTypeSetByUser?: boolean
}): OcrResult => {
    const ocrObjectAsPrinted = flipCreditNoteAmountSigns(ocrObject)
    if (shouldApplyOcrCreditNote({ ocrObject, expense, isNettingEnabled, isDocumentTypeSetByUser })) {
        return ocrObjectAsPrinted
    }
    return { ...ocrObjectAsPrinted, documentType: undefined }
}

const DOCUMENT_TYPE_LABEL_KEYS: Record<ExpenseDocumentTypeEnum, string> = {
    [ExpenseDocumentTypeEnum.INVOICE]: "input:request.document_type.invoice",
    [ExpenseDocumentTypeEnum.CREDIT_NOTE]: "input:request.document_type.credit_note",
}

const compareDocumentType = ({ expense, ocrObject, t }: Pick<CompareFormOcrDataParams, "expense" | "ocrObject" | "t">): RowInterface | undefined => {
    const originalDocumentType = expense.documentType ?? ExpenseDocumentTypeEnum.INVOICE
    if (!ocrObject.documentType || ocrObject.documentType === originalDocumentType) {
        return undefined
    }
    return {
        key: "documentType",
        field: "documentType",
        ocrData: ocrObject.documentType,
        formattedOriginalData: t(DOCUMENT_TYPE_LABEL_KEYS[originalDocumentType]),
        formattedOcrData: t(DOCUMENT_TYPE_LABEL_KEYS[ocrObject.documentType]),
        formattedField: t("input:request.document_type.label"),
    }
}
